'use client';
import { Button } from '@/components/ui/button';
import {
  Comparison,
  ComparisonHandle,
  ComparisonItem,
} from '@/components/ui/shadcn-io/comparison';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

const data = [
  {
    title: '图表',
    key: 'chart',
    image: {
      before:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/charts_before.jpg',
      after:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/charts_after.jpg',
    },
    heading: '图表翻译',
    subheading: '所见即所译，格式分毫不差',
    description:
      '支持复杂财报、学术论文、技术手册与商业报告中的全部图表类型。坐标轴文字、图例说明、数据点标注、表格标题、注释框、数学符号，全部精准翻译并原位还原。',
  },
  {
    title: '双栏',
    key: 'cols',
    image: {
      before:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/columns_before.jpg',
      after:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/columns_after.jpg',
    },
    heading: '双栏与多栏排版',
    subheading: '栏目边界精准识别，内容逐栏翻译',
    description:
      '完美处理学术期刊、法律合同、产品手册常见的双栏/多栏复杂排版。自动识别阅读顺序，跨栏标题、脚注、图表说明均准确对应，翻译后栏目结构、间距、对齐方式与原版完全一致。',
  },
  {
    title: '表格',
    key: 'table',
    image: {
      before:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/table_before.jpg',
      after:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/table_after.jpg',
    },
    heading: '表格翻译',
    subheading: '单元格结构完整保留，数据准确无误',
    description:
      '支持任意复杂度的表格，包括合并单元格、跨行跨列、嵌套表格、带颜色填充与条件格式的财务报表。表头、行标题、数值、备注全部精准翻译，边框、对齐方式、字体样式、数字格式原样复现。',
  },
  {
    title: '公式',
    key: 'formula',
    image: {
      before:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/formula_before.jpg',
      after:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/formula_after.jpg',
    },
    heading: '数学公式翻译',
    subheading: 'LaTeX 级别的识别与重构精度',
    description:
      '深度识别 Word 公式编辑器、LaTeX 渲染公式、图片公式以及手写公式。变量、上下标、分数、矩阵、积分符号等全部保留原始结构，翻译后仍可直接编辑与重新排版，满足顶级学术发表要求。',
  },
  {
    title: '图文混排',
    key: 'word',
    image: {
      before:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/word_before.jpg',
      after:
        'https://suppr-wilddatapublic-1320403996.cos.ap-shanghai.myqcloud.com/translate/showcase/img/word_after.jpg',
    },
    heading: '图文混排文档',
    subheading: '全局布局智能还原，无需二次调整',
    description:
      '图片与文字环绕、文本框叠放、页眉页脚、章节标题、编号列表、目录等所有排版元素均被完整保留。翻译后文档可直接打印、提交或存档，达到“看不出被翻译过”的终极效果。',
  },
];

export function TranslateComparison() {
  const [current, setCurrent] = useState(data[0]);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const handleImageLoad = (key: string) => {
    setLoadedImages((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <section className="relative bg-card pt-24 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 w-full gap-12">
          <div className="max-w-xl w-full mx-auto max-h-[600px] overflow-hidden relative">
            <Tabs defaultValue="chart">
              <div className="bg-muted rounded-xl py-2">
                <TabsList className="w-full">
                  {data.map((item) => (
                    <TabsTrigger
                      key={item.key}
                      value={item.key}
                      className="cursor-pointer"
                      onClick={() => {
                        setCurrent(item);
                      }}
                    >
                      {item.title}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {data.map((item) => (
                <TabsContent
                  key={item.key}
                  value={item.key}
                  className="relative"
                >
                  <Comparison className="lg:aspect-8/9 aspect-7/9 rounded-2xl">
                    <ComparisonItem
                      className={
                        loadedImages[item.key + '_before']
                          ? 'bg-neutral-500'
                          : ''
                      }
                      position="right"
                    >
                      <Image
                        alt="Placeholder 1"
                        className="opacity-80 dark:invert-[90] dark:hue-rotate-180"
                        width={1120}
                        height={1449}
                        src={item.image.before}
                        unoptimized
                        onLoad={() => handleImageLoad(item.key + '_before')}
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                    </ComparisonItem>
                    <ComparisonItem
                      className={
                        loadedImages[item.key + '_after']
                          ? 'bg-green-500 dark:bg-transparent'
                          : ''
                      }
                      position="left"
                    >
                      <Image
                        alt="Placeholder 2"
                        className="opacity-90 dark:invert-[90] dark:hue-rotate-180"
                        height={1440}
                        src={item.image.after}
                        unoptimized
                        width={2560}
                        onLoad={() => handleImageLoad(item.key + '_after')}
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                    </ComparisonItem>
                    <ComparisonHandle />
                  </Comparison>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          <div className="">
            <h2 className="text-5xl font-bold text-foreground tracking-tight">
              {current.heading}
            </h2>
            <p className="text-primary text-3xl mt-6">{current.subheading}</p>
            <p className="my-8 text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
              {current.description}
            </p>
            <Button
              asChild
              size="lg"
              className="h-12 mb-8 rounded-full px-8 bg-neutral-900 dark:bg-white text-background hover:bg-neutral-200 font-medium text-base border-0"
            >
              <Link href="/translate/upload">
                开始翻译
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
